import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  type OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Httpservice } from '../../services/httpservice';
import { ApiMethod, AuthEndPoints } from '../../helper/apiconstant';
import { environment } from '../../environments/environment';
import { encryptLoginPayload } from '../../helper/login-crypto';
import { SignInPayload, SignInResponseValue } from './signin.interfaces';
import { Commonservice } from '../../services/commonservice';
import { ManageSession } from '../../services/manage-session';
import { SessionService } from '../../services/session.service';
import { ServerTimeService } from '../../services/server-time';

@Component({
  selector: 'app-signin',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './signin.html',
  styleUrl: './signin.scss',
})
export class Signin implements OnInit {
  @ViewChild('demoMenu') demoMenu?: ElementRef<HTMLDivElement>;

  protected readonly logoPath =
    'assets/images/authentication/nea-logo.png';

  constructor(
    private readonly router: Router, 
    private readonly httpSvc: Httpservice,
    private readonly commonSvc: Commonservice,
    private readonly sessionSvc: ManageSession,
    private readonly appSessionSvc: SessionService,
    private readonly serverTimeSvc: ServerTimeService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  protected username = '';
  protected password = '';
  protected error = '';
  protected showPassword = false;
  protected rememberMe = false;
  protected loginSuccess = false;

  ngOnInit(): void {
    const remembered = this.sessionSvc.getRememberMeCredentials();
    if (remembered) {
      this.username = remembered.username;
      this.password = remembered.password;
      this.rememberMe = true;
    }

    if (this.commonSvc.isLoggedIn) {
      const isVerified = this.appSessionSvc.getVerificationState();
      this.router.navigate([isVerified === false ? '/update-password' : '/dashboard']);
      return;
    }
  }

  protected async onSignin(): Promise<void> {
    this.error = '';
    this.loginSuccess = false;

    if (!this.username || !this.password) {
      this.error = 'Please enter both username and password';
      return;
    }
    const payload: SignInPayload = {
      username: this.username,
      password: this.password,
    };

    let encryptedPayload: SignInPayload;
    try {
      encryptedPayload = await encryptLoginPayload(payload, environment.loginPublicKey);
    } catch {
      this.error = 'Unable to secure login request. Please try again.';
      this.cdr.markForCheck();
      return;
    }

    this.httpSvc.requestCall(AuthEndPoints.LOGIN, ApiMethod.POST, encryptedPayload)
    .subscribe((data) => {
      const response = data?.value as SignInResponseValue | undefined;
      const authenticated = this.isSigninAuthenticated(data, response);
      const token = this.extractAuthToken(data, response);

      if (authenticated && token) {
        this.loginSuccess = true;
        this.serverTimeSvc.initialize(response?.current_time, response?.current_time_bs);
        this.commonSvc.setAuthContext(
          token,
          response?.menu_json ?? null,
          response?.is_verified ?? null,
          {
            user_id: response?.user_id ?? null,
            username: response?.username ?? null,
            user_fullname: response?.user_fullname ?? null,
            email: response?.email ?? null,
            mobile_no: response?.mobile_no ?? null,
            role_name: response?.role_name ?? null,
            profile_image: response?.profile_image ?? null,
          },
        );
        if (this.rememberMe) {
          this.sessionSvc.saveRememberMeCredentials({
            username: this.username,
            password: this.password,
          });
        } else {
          this.sessionSvc.clearRememberMeCredentials();
        }
        this.router.navigate([response?.is_verified === false ? '/update-password' : '/dashboard']);
      } else {
        this.commonSvc.clearSession();
        this.error = this.resolveSigninErrorMessage(data);
        this.cdr.markForCheck();
      }
    }, (error) => {
      this.commonSvc.clearSession();
      this.error = this.resolveSigninErrorMessage(error?.error ?? error);
      this.cdr.markForCheck();
    });
  }

  private isSigninAuthenticated(response: any, value: any): boolean {
    const status = typeof response?.status === 'string' ? response.status.toUpperCase() : '';

    if (status === 'TRUE') {
      return Boolean(value?.authenticated);
    }

    if (status === 'FALSE') {
      return false;
    }

    return Boolean(value?.authenticated);
  }

  private extractAuthToken(response: any, value?: SignInResponseValue): string {
    const token = value?.token ?? value?.user_token ?? response?.token ?? response?.user_token ?? '';
    return typeof token === 'string' ? token.trim() : '';
  }

  private resolveSigninErrorMessage(response: any): string {
    const message = typeof response?.msg === 'string' ? response.msg : '';
    const authenticated = response?.value?.authenticated;

    if (message) {
      return message;
    }

    if (authenticated === false) {
      return 'Invalid username or password';
    }

    return 'Invalid username or password';
  }

  protected togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  protected goToForgotPassword(): void {
    this.router.navigate(['/forgot-password']);
  }

  protected onChangeRememberMe(checked: boolean) {
    this.rememberMe = checked;
    if (!checked) {
      this.sessionSvc.clearRememberMeCredentials();
    }
  }
}
